import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { ICluster } from '~/types/cluster'

import Button from '~/components/Buttons/Button'
import CreatableSelectInput from '~/components/Inputs/ReactHookForm/CreatableSelectInput'
import Modal from '~/components/Modal'
import { ModalFooter, ModalHeader, ModalBody } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import usePreventScroll from '~/hooks/usePreventScroll'
import { keys as clustersKeys, useSetClusterTagsMutation } from '~/queries/clusters'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'
import { RequiredYupOption } from '~/utils/yup'

import './styles.scss'

const MODAL_ID = 'edit-cluster-tags-modal'

interface FormValues {
  tags: IOption[]
}

const validationSchema = Yup.object().shape({
  tags: Yup.array().of(RequiredYupOption).required(FORM_ERRORS.REQUIRED),
})

interface Props {
  cluster: ICluster
  clusterTags: string[]
  isOpen: boolean
  onClose: VoidFunction
  onSuccess: (data: { clusterId: string; tags: string[] }) => void
}

const EditClusterTagsModal = ({ cluster, clusterTags, isOpen, onClose, onSuccess }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: { tags: cluster.tags?.map((tag) => ({ label: tag, value: tag })) || [] },
    resolver: yupResolver(validationSchema),
  })

  const options = clusterTags
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((el) => ({ label: el, value: el }))

  /** Queries & Mutations */

  const { mutateAsync: setClusterTags } = useSetClusterTagsMutation()

  /** Effects */

  usePreventScroll(isOpen)

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { tags } = values
    const newTags = tags.map((t) => t.value)

    try {
      await setClusterTags({ clusterId: cluster.cluster_id, tags: newTags })
      await queryClient.invalidateQueries({ queryKey: clustersKeys.clustersTags() })
      onSuccess({ clusterId: cluster.cluster_id, tags: newTags })
      displayToast('Tags successfully edited', 'success')
    } catch (e) {
      captureException(e)
      displayToast(`Tags could not be edited`, 'error')
    }
  }

  const handleModalClose = () => {
    reset()
    onClose()
  }

  /** Render */

  return (
    <Modal className="edit-cluster-tags-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={handleModalClose}>
      <ModalHeader>Edit cluster tags</ModalHeader>
      <form className="edit-cluster-tags-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody className="edit-cluster-tags-form__modal-body">
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                isMulti
                label="Tags"
                options={options}
                placeholder='Type a tag and press "Enter"'
              />
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            Update
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default EditClusterTagsModal
